import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    computed,
    signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Étapes affichées par la checklist de préparation. Elles correspondent
 * aux traitements réellement effectués par le moteur à la préparation du
 * test (profil enrichi, pools de scénarios, plan/calibrage, prégénération
 * du premier exercice) — l'animation est honnête, seule la dernière étape
 * attend la confirmation réelle.
 */
const CHECKLIST_STEP_KEYS = [
    'ai-exam-checklist.step-profile',
    'ai-exam-checklist.step-scenarios',
    'ai-exam-checklist.step-calibration',
    'ai-exam-checklist.step-first-exercise',
] as const;

/** Délai d'animation entre deux étapes intermédiaires (ms). */
const STEP_ANIMATION_INTERVAL_MS = 2200;

/**
 * Écran de préparation du test avec checklist animée (chantier 8 —
 * artifice d'attente n°2), affiché entre le clic « démarrer » et la
 * livraison du premier exercice.
 *
 * Les étapes intermédiaires s'animent sur des temps fixes ; la dernière
 * (« génération du premier exercice ») reste en cours tant que le parent
 * ne passe pas [ready] à true (réponse start-test reçue).
 *
 * Usage :
 * <lib-ai-exam-preparation-checklist [ready]="checklistReady()">
 * </lib-ai-exam-preparation-checklist>
 */
@Component({
    selector: 'lib-ai-exam-preparation-checklist',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatProgressSpinnerModule, TranslateModule],
    templateUrl: './AI-exam-preparation-checklist.component.html',
    styleUrls: ['./AI-exam-preparation-checklist.component.scss'],
})
export class AIExamPreparationChecklistComponent implements OnInit, OnDestroy {
    /** Passe à true quand le premier exercice est effectivement livré. */
    @Input() set ready(value: boolean) {
        this.isReady.set(value);
    }

    stepKeys = CHECKLIST_STEP_KEYS;

    private isReady = signal(false);
    /** Nombre d'étapes intermédiaires complétées par l'animation. */
    private animatedSteps = signal(0);
    private animationInterval: ReturnType<typeof setInterval> | null = null;

    /** Index de l'étape courante (les précédentes sont cochées). */
    currentStepIndex = computed(() => {
        if (this.isReady()) {
            return this.stepKeys.length;
        }
        // La dernière étape n'est jamais complétée par la seule animation.
        return Math.min(this.animatedSteps(), this.stepKeys.length - 1);
    });

    ngOnInit(): void {
        this.animationInterval = setInterval(() => {
            if (this.animatedSteps() < this.stepKeys.length - 1) {
                this.animatedSteps.set(this.animatedSteps() + 1);
            }
        }, STEP_ANIMATION_INTERVAL_MS);
    }

    ngOnDestroy(): void {
        if (this.animationInterval !== null) {
            clearInterval(this.animationInterval);
            this.animationInterval = null;
        }
    }

    stepState(index: number): 'done' | 'current' | 'pending' {
        const current = this.currentStepIndex();
        if (index < current) {
            return 'done';
        }
        return index === current ? 'current' : 'pending';
    }
}
